import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button, Panel } from '../../components';
import { useRequestPasswordReset } from './useAuth';
import { safeNextPath } from '../../lib/nextPath';

/**
 * Step one of the forgot-password flow: collect an identifier, ask nauth to
 * send a reset code, and say the same thing back no matter what nauth actually
 * did.
 *
 * The whole screen is built around one constraint: `POST /auth/forgot-password`
 * is enumeration-safe on the SERVER (it returns `{success:true}` whether or not
 * the account exists — see the trap notes on `requestPasswordReset` in ./api),
 * and nothing here may undo that by behaving differently for the two cases.
 * Concretely: the mutation's resolved value is never read (its type is
 * `void`), the success panel is shown from `isSuccess` alone, and the "Enter
 * the code" link always appears — because showing it only when an email was
 * "really" sent would be the client-side version of the exact oracle the API
 * was built to avoid. The one thing this page cannot fix is a timing
 * difference: actually sending mail (the "exists" branch) does real work a
 * dead-end lookup does not, so a sufficiently patient attacker could still time
 * the two paths apart. That is a server-side property of `AuthService.
 * forgotPassword`, out of reach from this file, and is flagged back to the
 * lead as a residual risk rather than silently assumed away.
 */
export function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get('next'));
  const requestReset = useRequestPasswordReset();

  const [identifier, setIdentifier] = useState('');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (identifier.trim() === '') return;
    requestReset.mutate(identifier.trim());
  };

  const resetPath = `/reset-password?identifier=${encodeURIComponent(identifier.trim())}&next=${encodeURIComponent(next)}`;

  return (
    <main className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-surface px-4 py-10">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-0 h-[26.25rem] w-[45rem] -translate-x-1/2 -translate-y-1/3 rounded-full bg-accent-tint blur-3xl"
      />

      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <h1 className="sr-only">Reset your Rundown password</h1>
          <img src="/rundown-logo.png" alt="Rundown — AI project management" className="h-20 w-auto" />
          <p className="mt-4 fs-13 text-muted">The shared portal for your project.</p>
        </div>

        <Panel className="shadow-sm">
          {requestReset.isSuccess ? (
            <div className="flex flex-col gap-4 text-center">
              <p className="fs-14 font-semibold text-ink">Check your inbox</p>
              <p className="fs-13 leading-relaxed text-muted">
                If an account exists for <span className="font-medium text-ink-soft">{identifier}</span>,
                we&apos;ve sent a message with a verification code. It can take a minute to arrive — check
                spam if you don&apos;t see it.
              </p>
              <Link to={resetPath} className="fs-13 font-semibold text-accent hover:underline">
                I have a code →
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <p className="mb-1 fs-13 text-muted">
                Enter the email on your account and we&apos;ll send a code to reset your password.
              </p>

              <label className="flex flex-col gap-1">
                <span className="fs-12 font-medium text-ink-soft">Email</span>
                <input
                  type="email"
                  name="identifier"
                  autoComplete="email"
                  autoFocus
                  required
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  className="h-10 rounded-lg border border-line-stronger bg-base px-3 text-sm text-ink placeholder:text-faint transition-colors focus:border-accent"
                  placeholder="you@company.com"
                />
              </label>

              {/* Deliberately NOT branching this on `requestReset.error.status`
                  beyond "did the request even leave the browser". A 400
                  (malformed identifier) and a 200-for-a-missing-account both
                  read as ordinary outcomes of the same form; only a transport
                  failure is worth telling the user apart from "submitted". */}
              {requestReset.isError && (
                <p role="alert" className="rounded-md bg-danger-tint px-3 py-2 fs-13 text-danger">
                  Could not reach the server. Try again.
                </p>
              )}

              <Button
                type="submit"
                variant="secondary"
                disabled={requestReset.isPending}
                className="mt-1 h-10 w-full"
              >
                {requestReset.isPending ? 'Sending…' : 'Send reset code'}
              </Button>
            </form>
          )}
        </Panel>

        <p className="mt-6 text-center fs-12 text-faint">
          <Link to={`/signin?next=${encodeURIComponent(next)}`} className="hover:text-muted hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </main>
  );
}
